import type * as Bridge from "@shopfront/bridge";
import { ApplicationEventsFromShopfront } from "../Applications/ApplicationEvents";
import {
    type ApplicationManagerEventData,
    InternalApplicationEvents,
} from "../Applications/ApplicationManager";
import { getApplicationManager } from "../Applications/ApplicationUtilities";

export enum FulfilmentStatus {
    Enabled = "ENABLED",
    Disabled = "DISABLED",
}

export enum FulfilmentEvents {
    StatusChange = "FulfilmentStatusChange",
    OrdersChange = "FulfilmentOrdersChange",
    OrderCreate = "FulfilmentOrderCreate",
    OrderUpdate = "FulfilmentOrderUpdate",
    OrderDelete = "FulfilmentOrderDelete",
}

export interface FulfilmentOptions {
    requireApproval: boolean;
}

type FulfilmentEventArguments = {
    [FulfilmentEvents.StatusChange]: [FulfilmentStatus];
    [FulfilmentEvents.OrdersChange]: [];
    [FulfilmentEvents.OrderCreate]: [id: string];
    [FulfilmentEvents.OrderUpdate]: [id: string];
    [FulfilmentEvents.OrderDelete]: [id: string];
};

type FulfilmentEventCallback<E extends FulfilmentEvents> = (...a: FulfilmentEventArguments[E]) => void;

export type FulfilmentOrderStatus = Bridge.OrderStatus;
export type FulfilmentOrder = Bridge.OrderSummaryDetails;
export type FulfilmentOrderCreate = Omit<FulfilmentOrder, "status"> & Partial<Pick<FulfilmentOrder, "status">>;
export type FulfilmentItem = Bridge.OrderItem;

export interface FulfilmentOrderDetailed extends Bridge.OrderDetails {
    items: Array<FulfilmentItem>;
}

export interface InternalFulfilmentOrderDetailed extends Bridge.OrderDetails {
    integration: string;
}

export interface InternalFulfilmentOrder extends Bridge.OrderSummaryDetails {
    internalId: string;
    integration: string;
}

const DEFAULT_OPTIONS: FulfilmentOptions = {
    requireApproval: false,
};

export type FilterMap = Record<FulfilmentOrderStatus, boolean>;

export function getFilterMap(filters: Array<FulfilmentOrderStatus>): FilterMap {
    const filterMap: Record<FulfilmentOrderStatus, boolean> = {
        COLLECTED          : false,
        COMPLETED          : false,
        PACKED             : false,
        PENDING_APPROVAL   : false,
        WAITING_FOR_PACKING: false,
    };

    for(const filter of filters) {
        if(filter in filterMap) {
            filterMap[filter] = true;
        }
    }

    return filterMap;
}

export default class FulfilmentManager {
    protected status: FulfilmentStatus = FulfilmentStatus.Disabled;
    protected orders = new Map<string, InternalFulfilmentOrder>();
    protected options = new Map<string, FulfilmentOptions>();

    private constructor() {
        getApplicationManager().addEventListener(
            InternalApplicationEvents.APPLICATION_DESTROYED,
            this.handleRevokeEvent.bind(this)
        );
    }

    public getStatus(): FulfilmentStatus {
        return this.status;
    }

    /**
     * Initialise fulfilment for the integration
     */
    public init(integration: string, options?: FulfilmentOptions): void {
        // Check if integration has already been set up
        if(this.options.has(integration)) {
            return;
        }

        if(this.status === FulfilmentStatus.Disabled) {
            this.status = FulfilmentStatus.Enabled;
            this.emit(FulfilmentEvents.StatusChange, this.status);
        }

        if(typeof options === "undefined") {
            this.options.set(integration, { ...DEFAULT_OPTIONS });
        } else {
            this.options.set(integration, options);
        }
    }

    /**
     * Get the fulfilment options for an integration
     * @param integration
     * @protected
     */
    protected getOptions(integration: string): FulfilmentOptions {
        const options = this.options.get(integration);

        if(typeof options === "undefined") {
            throw new TypeError("Integration has not been set-up for fulfilment.");
        }

        return options;
    }

    /**
     * Get the integrations that have opt-in to fulfilment
     */
    public getOptIn(): Array<[ string, FulfilmentOptions ]> {
        const optedIn: Array<[ string, FulfilmentOptions ]> = [];

        for(const [ key, value ] of this.options.entries()) {
            optedIn.push([ key, value ]);
        }

        return optedIn;
    }

    /**
     * Get the orders matching the filters supplied, if no filter array is given all orders are returned
     * @param filters
     */
    public getOrders(filters?: Array<FulfilmentOrderStatus>): Array<InternalFulfilmentOrder> {
        if(!Array.isArray(filters)) {
            return [ ...this.orders.values() ];
        }

        const filterMap = getFilterMap(filters);

        const results: Array<InternalFulfilmentOrder> = [];

        for(const order of this.orders.values()) {
            if(filterMap[order.status]) {
                results.push(order);
            }
        }

        return results;
    }

    /**
     * Gets the full order details from the integration application
     * @param internalId This is the id that Fulfilment manager makes not the one from the application
     */
    public async getOrder(internalId: string): Promise<InternalFulfilmentOrderDetailed> {
        const order = this.orders.get(internalId);

        if(typeof order === "undefined") {
            throw new Error("Order not found.");
        }

        const application = getApplicationManager().getApplication(order.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        const { data } = await application.send<
            Omit<FulfilmentOrderDetailed, "id">
        >(ApplicationEventsFromShopfront.FULFILMENT_GET_ORDER, order.id);

        return {
            id         : order.id,
            integration: order.integration,
            ...data,
        };
    }

    /**
     * Synchronise the orders from the integration
     * @param integration
     * @param orders
     * @param merge if false, clear all the orders from this integration before adding the new orders
     */
    public syncOrders(integration: string, orders: Array<FulfilmentOrderCreate>, merge: boolean): void {
        this.init(integration);

        const options = this.getOptions(integration);

        const defaultStatus: FulfilmentOrderStatus = (
            options.requireApproval ?
                "PENDING_APPROVAL" :
                "WAITING_FOR_PACKING"
        );

        // Map existing orders from the integrations
        const existing = new Map<string, boolean>();

        for(const key of this.orders.keys()) {
            if(key.startsWith(integration)) {
                existing.set(key, true);

                // Remove order from the integration
                if(!merge) {
                    this.orders.delete(key);
                }
            }
        }

        // Prepare a list of orders to auto-approve
        const approve: Array<string> = [];

        for(const order of orders) {
            const key = this.buildKey(integration, order.id);

            const internalOrder: InternalFulfilmentOrder = {
                internalId: key,
                integration,
                ...order,
                status    : (typeof order.status === "undefined" ? defaultStatus : order.status),
            };

            if(internalOrder.status === "PENDING_APPROVAL" && !options.requireApproval) {
                internalOrder.status = "WAITING_FOR_PACKING";
                approve.push(internalOrder.id);
            }

            this.orders.set(key, internalOrder);
        }

        // Handle marking as approved
        if(approve.length) {
            const application = getApplicationManager().getApplication(integration);

            if(application === null) {
                throw new Error("Unable to send order approvals to integration.");
            }

            for(const id of approve) {
                void application.send(ApplicationEventsFromShopfront.FULFILMENT_ORDER_APPROVAL, {
                    approved: true,
                    id,
                });
            }
        }

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Create order from the integration, if the order status isn't supplied the default value will
     * be set based on requireApproval setting
     * @param integration
     * @param order
     */
    public createOrder(integration: string, order: FulfilmentOrderCreate): void {
        this.init(integration);

        const options = this.getOptions(integration);
        const defaultStatus: FulfilmentOrderStatus = (
            options.requireApproval ?
                "PENDING_APPROVAL" :
                "WAITING_FOR_PACKING"
        );

        const key = this.buildKey(integration, order.id);

        if(this.orders.has(key)) {
            throw new Error("Order has already been created.");
        }

        this.setOrder(integration, key, {
            internalId: key,
            integration,
            ...order,
            status    : (typeof order.status === "undefined" ? defaultStatus : order.status),
        });

        this.emit(FulfilmentEvents.OrderCreate, key);
    }

    /**
     * Update order from the integration
     * @param integration
     * @param order
     */
    public updateOrder(integration: string, order: FulfilmentOrder): void {
        this.init(integration);

        const key = this.buildKey(integration, order.id);

        if(!this.orders.has(key)) {
            throw new Error("Order doesn't exist.");
        }

        this.setOrder(integration, key, {
            ...order,
            internalId: key,
            integration,
        });

        this.emit(FulfilmentEvents.OrderUpdate, key);
    }

    /**
     * Handle setting the order in the list
     * @param integration
     * @param key
     * @param order
     * @protected
     */
    protected setOrder(integration: string, key: string, order: InternalFulfilmentOrder): void {
        const options = this.getOptions(integration);

        let autoApprove = false;

        if(order.status === "PENDING_APPROVAL" && !options.requireApproval) {
            order.status = "WAITING_FOR_PACKING";
            autoApprove = true;
        }

        this.orders.set(key, order);

        if(autoApprove) {
            const application = getApplicationManager().getApplication(integration);

            if(application === null) {
                throw new Error("Unable to send order approvals to integration.");
            }

            void application.send(ApplicationEventsFromShopfront.FULFILMENT_ORDER_APPROVAL, {
                id      : order.id,
                approved: true,
            });
        }

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Delete the order
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public deleteOrder(integration: string, id?: string): void {
        this.init(integration);

        let key = integration;

        if(typeof id !== "undefined") {
            key = this.buildKey(integration, id);
        }

        if(this.orders.delete(key)) {
            this.emit(FulfilmentEvents.OrdersChange);
            this.emit(FulfilmentEvents.OrderDelete, key);
        }
    }

    /**
     * Handles sending approval status of the order to the integration
     * @param approved
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public sendOrderApproval(approved: boolean, integration: string, id?: string): void {
        let key = integration;

        if(typeof id !== "undefined") {
            key = this.buildKey(integration, id);
        }

        const pending = this.orders.get(key);

        if(!pending) {
            throw new Error("Order not found.");
        }

        if(pending.status !== "PENDING_APPROVAL") {
            throw new Error("Order already approved.");
        }

        if(approved) {
            this.orders.set(key, {
                ...pending,
                status: "WAITING_FOR_PACKING",
            });
        } else {
            this.orders.delete(key);
        }

        const application = getApplicationManager().getApplication(pending.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        void application.send(ApplicationEventsFromShopfront.FULFILMENT_ORDER_APPROVAL, {
            id: pending.id,
            approved,
        });

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Sends a request to the integration to void the order
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public voidOrder(integration: string, id?: string): void {
        const [ key, order ] = this.getOrderSummary(integration, id);

        if(!order) {
            throw new Error("Order not found.");
        }

        const application = getApplicationManager().getApplication(order.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        void application.send(ApplicationEventsFromShopfront.FULFILMENT_VOID_ORDER, order.id);

        this.orders.delete(key);

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Tells the integration was sale was created from the order
     * @param sale built sale state for the integration
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public processOrder(sale: Bridge.ShopfrontSaleState, integration: string, id?: string): void {
        const [ key, order ] = this.getOrderSummary(integration, id);

        if(!order) {
            throw new Error("Order not found.");
        }

        const application = getApplicationManager().getApplication(order.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        void application.send(ApplicationEventsFromShopfront.FULFILMENT_PROCESS_ORDER, {
            id: order.id,
            sale,
        });

        this.orders.set(key, {
            ...order,
            status: "PACKED",
        });

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Tells the integration the order has been collected
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public markAsCollected(integration: string, id?: string): void {
        const [ key, order ] = this.getOrderSummary(integration, id);

        if(!order) {
            throw new Error("Order not found.");
        }

        const application = getApplicationManager().getApplication(order.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        void application.send(ApplicationEventsFromShopfront.FULFILMENT_ORDER_COLLECTED, order.id);

        this.orders.set(key, {
            ...order,
            status: "COLLECTED",
        });

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Tells the integration the order has been completed
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public markAsCompleted(integration: string, id?: string): void {
        const [ key, order ] = this.getOrderSummary(integration, id);

        if(!order) {
            throw new Error("Order not found.");
        }

        const application = getApplicationManager().getApplication(order.integration);

        if(application === null) {
            throw new Error("Order is in invalid state.");
        }

        void application.send(ApplicationEventsFromShopfront.FULFILMENT_ORDER_COMPLETED, order.id);

        this.orders.set(key, {
            ...order,
            status: "COMPLETED",
        });

        this.emit(FulfilmentEvents.OrdersChange);
    }

    /**
     * Get the integration order
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public getOrderSummary(integration: string, id?: string): [
        key: string,
        order: InternalFulfilmentOrder | undefined,
    ] {
        let key = integration;

        if(typeof id !== "undefined") {
            key = this.buildKey(integration, id);
        }

        return [ key, this.orders.get(key) ];
    }

    /**
     * Check if the fulfilment integration has an order
     * @param integration this is either the internal key or integration is external id is supplied
     * @param id
     */
    public hasOrder(integration: string, id?: string): boolean {
        let key = integration;

        if(typeof id !== "undefined") {
            key = this.buildKey(integration, id);
        }

        return this.orders.has(key);
    }

    /**
     * Build a key to identify an order with
     * @param integration
     * @param id
     * @protected
     */
    protected buildKey(integration: string, id: string): string {
        return `${integration}-${id}`;
    }

    /**
     * Handle an integration removing itself from the Fulfilment API
     * @param event
     * @protected
     */
    protected handleRevokeEvent(event: ApplicationManagerEventData<unknown>): void {
        this.removeIntegration(event.application.id);
    }

    /**
     * Remove all the fulfilment details related to the supplied integration id
     * @param id
     * @protected
     */
    protected removeIntegration(id: string): void {
        // Check to see if integration has been set up
        if(!this.options.has(id)) {
            return;
        }

        // Remove the integration orders
        let hasChanges = false;

        for(const order of this.orders.values()) {
            if(order.integration === id) {
                hasChanges = true;

                this.orders.delete(order.internalId);
                this.emit(FulfilmentEvents.OrderDelete, order.internalId);
            }
        }

        // Emit that order list has changed
        if(hasChanges) {
            this.emit(FulfilmentEvents.OrdersChange);
        }

        // Remove the integration options
        this.options.delete(id);

        // Check to see if any integrations are remaining
        if(this.options.size === 0) {
            this.status = FulfilmentStatus.Disabled;
            this.emit(FulfilmentEvents.StatusChange, this.status);
        }
    }

    // Setup listener handling
    protected listeners: {
        [E in FulfilmentEvents]: Set<FulfilmentEventCallback<E>>
    } = {
        [FulfilmentEvents.StatusChange]: new Set(),
        [FulfilmentEvents.OrdersChange]: new Set(),
        [FulfilmentEvents.OrderCreate] : new Set(),
        [FulfilmentEvents.OrderUpdate] : new Set(),
        [FulfilmentEvents.OrderDelete] : new Set(),
    };

    public addListener<E extends FulfilmentEvents>(event: E, callback: FulfilmentEventCallback<E>): void {
        if(!(event in this.listeners)) {
            throw new TypeError("Invalid event received");
        }

        this.listeners[event].add(callback);
    }

    public removeListener<E extends FulfilmentEvents>(event: E, callback: FulfilmentEventCallback<E>): void {
        if(!(event in this.listeners)) {
            throw new TypeError("Invalid event received");
        }

        this.listeners[event].delete(callback);
    }

    protected emit<E extends FulfilmentEvents>(event: E, ...args: FulfilmentEventArguments[E]): void {
        if(!(event in this.listeners)) {
            throw new TypeError("Invalid event received");
        }

        for(const listener of this.listeners[event]) {
            listener(...args);
        }
    }

    // Singleton handling
    private static instance: FulfilmentManager;
    public static getInstance(): FulfilmentManager {
        if(typeof this.instance === "undefined") {
            this.instance = new FulfilmentManager();
        }

        return this.instance;
    }
}
